import { z } from 'zod'

import { InspectionBinBoundsSchema } from './inspectionBinBounds'
import { ReportViewSchema } from './reportViewSchema'

const InspectionFindingSchema = z.object({
  id: z.string(),
  kind: z.literal('low_area_minimum'),
  index: z.number().int().positive(),
  course: z.string().nullish(),
  bin_bounds: InspectionBinBoundsSchema,
})
export type InspectionFinding = z.infer<typeof InspectionFindingSchema>
export const InspectionViewContextSchema = z.object({
  site_id: z.string(),
  unit_id: z.string(),
  component_id: z.string(),
  inspection_id: z.string(),
  dataset_id: z.string(),
  asset_model_id: z.string(),
  column: z.string().optional(),
  dataset_generation: z.number().int().optional(),
  asset_model_generation: z.number().int().optional(),
  layer: z.enum([
    'thickness',
    'corrosion_rate',
    'metal_loss',
    'remaining_life',
    'minimum_thickness',
    'remaining_corrosion_allowance',
  ]),
  bin_bounds: InspectionBinBoundsSchema.nullish(),
  as_of_date: z.string().optional(),
})
export type InspectionViewContext = z.infer<typeof InspectionViewContextSchema>

const Count = z.number().int().nonnegative().nullish()
const InspectionProvenanceSchema = z.object({
  inspection_id: z.string().nullish(),
  inspection_date: z.string().nullish(),
  dataset_id: z.string().nullish(),
  dataset_generation: z.number().int().nullish(),
  publishing_state: z.literal('finalized').optional(),
  column: z.string().nullish(),
  asset_model_id: z.string().nullish(),
  asset_model_generation: z.number().int().nullish(),
  forecast_config_id: z.string().nullish(),
  forecast_config_generation: z.number().int().nullish(),
  as_of_date: z.string().nullish(),
  upper_limit_years_at_inspection: z.number().optional(),
  minimum_floor_years: z.number().optional(),
  bin_bounds: z
    .object({
      x_start: z.number().int(),
      x_end: z.number().int(),
      y_start: z.number().int(),
      y_end: z.number().int(),
    })
    .nullish(),
})

const InspectionViewSchema = z.object({
  report: ReportViewSchema.nullish(),
  kind: z.enum(['remaining_life', 'inspection_view']),
  unit_label: z.string().nullish(),
  presentation: z
    .object({
      title: z.string().max(120).nullish(),
      summary: z.string().max(800).nullish(),
      layer: z.enum([
        'thickness',
        'corrosion_rate',
        'metal_loss',
        'remaining_life',
        'minimum_thickness',
        'remaining_corrosion_allowance',
      ]),
      mode: z.enum(['2D', '3D']).default('3D'),
      focus: z.enum(['whole', 'minimum', 'maximum']).default('whole'),
      show_features: z.boolean().default(true),
    })
    .nullish(),
  component_label: z.string().nullish(),
  unit_id: z.string().nullish(),
  site_id: z.string().nullish(),
  organization_id: z.string().nullish(),
  component_id: z.string().nullish(),
  provenance: InspectionProvenanceSchema.nullish(),
  calculation_error: z.string().nullish(),
  findings: z.array(InspectionFindingSchema).max(100).optional(),
  selected_finding_id: z.string().nullish(),
  calculation: z
    .object({
      result: z.object({
        component_id: z.string(),
        min_remaining_life_years: z.number().nullish(),
        bins_total: Count,
        bins_excluded: Count,
        bins_at_upper_limit: Count,
        bins_below_tmin: Count,
        error: z.string().nullish(),
      }),
      provenance: InspectionProvenanceSchema,
    })
    .nullish(),
})

export type InspectionResult = z.infer<typeof InspectionViewSchema>

const NiiResultSchema = z.object({
  report: ReportViewSchema.nullish(),
  kind: z.literal('nii_assessment'),
  presentation: z.object({
    title: z.string().max(120).nullish(),
    summary: z.string().max(800).nullish(),
    sections: z
      .array(
        z.enum([
          'summary',
          'conclusions',
          'recommendations',
          'zones',
          'statistics',
        ])
      )
      .max(5),
    section_titles: z
      .object({
        summary: z.string().max(120).nullish(),
        conclusions: z.string().max(120).nullish(),
        recommendations: z.string().max(120).nullish(),
        zones: z.string().max(120).nullish(),
        statistics: z.string().max(120).nullish(),
      })
      .optional(),
    zone_layout: z.enum(['table', 'cards']).optional(),
    zone_columns: z
      .array(
        z.enum(['strategy', 'damage_mechanisms', 'recommendations', 'area'])
      )
      .max(4)
      .optional(),
    charts: z
      .array(z.enum(['strategy', 'damage_mechanism', 'zone_area']))
      .max(3)
      .optional(),
  }),
  ticket_id: z.string(),
  workflow_id: z.string(),
  site_id: z.string(),
  organization_id: z.string().nullish(),
  unit_id: z.string().nullish(),
  unit_label: z.string().nullish(),
  title: z.string(),
  status: z.string().nullish(),
  workflow_complete: z.boolean(),
  version: z.number().int().nullish(),
  analysis_date: z.string().nullish(),
  eligibility: z.string().nullish(),
  executive_summary: z.string().nullish(),
  computed_eligibility: z.string().nullish(),
  justification: z.string().nullish(),
  recommendation_items: z.array(z.string()).optional(),
  zone_count: z.number().int().nonnegative().nullish(),
  statistics: z
    .array(
      z.object({
        metric: z.enum(['strategy', 'damage_mechanism', 'zone_area']),
        unit: z.enum(['zones', 'm2']),
        points: z
          .array(
            z.object({
              label: z.string(),
              value: z.number().finite().nonnegative(),
            })
          )
          .max(100),
        missing_zones: z.number().int().nonnegative(),
        truncated: z.boolean(),
      })
    )
    .optional(),
  recommendations: z.string().nullish(),
  zones: z.array(
    z.object({
      title: z.string(),
      strategy: z.string().nullish(),
      area_m2: z.number().finite().nonnegative().nullish(),
      damage_mechanisms: z.array(z.string()),
      recommendations: z.array(z.string()),
    })
  ),
  zones_truncated: z.boolean(),
})
export const InspectionResultSchema = z.discriminatedUnion('kind', [
  InspectionViewSchema,
  NiiResultSchema,
  z.object({
    kind: z.literal('report'),
    site_id: z.string(),
    title: z.string(),
    presentation: z.object({
      title: z.string().max(120).nullish(),
      summary: z.string().max(800).nullish(),
    }),
    report: ReportViewSchema,
  }),
])
export type NiiResult = z.infer<typeof NiiResultSchema>
export type InspectionPreviewResult = z.infer<typeof InspectionResultSchema>

export function inspectionProvenance(result: InspectionResult) {
  return result.provenance ?? result.calculation?.provenance ?? {}
}

export function inspectionComponentId(result: InspectionResult) {
  return result.component_id ?? result.calculation?.result.component_id
}
